import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { UserCog, Mail, TrendingUp, Phone, Edit, Trash2, Plus, Key } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'SUPPORT',
    phone: ''
  });

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listStaff();
      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];
      setStaff(items);
    } catch (error) {
      console.error('Error loading staff:', error);
      toast.error('Impossible de charger les conseillers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const [firstName, ...rest] = (formData.name || '').trim().split(' ');
      const lastName = rest.join(' ').trim();
      const payload = {
        email: formData.email,
        first_name: firstName || formData.name || 'Prénom',
        last_name: lastName || firstName || 'Nom',
        role: formData.role,
        phone: formData.phone
      };

      if (editingStaff) {
        await adminAPI.updateStaff(editingStaff.id, payload);
        toast.success('Conseiller mis à jour');
      } else {
        const response = await adminAPI.createStaff(payload);
        const tempPassword = response.data?.temp_password;
        
        if (tempPassword) {
          toast.success(
            `Conseiller créé ! Mot de passe temporaire : ${tempPassword}`,
            { duration: 10000 }
          );
          // Also show in alert for easy copy
          alert(
            `Conseiller créé avec succès !\n\n` +
            `Email: ${formData.email}\n` +
            `Mot de passe temporaire: ${tempPassword}\n\n` +
            `Un email de bienvenue a également été envoyé avec ces informations.`
          );
        } else {
          toast.success('Conseiller créé. Un email a été envoyé.');
        }
      }
      setDialogOpen(false);
      resetForm();
      loadStaff();
    } catch (error) {
      console.error('Error saving staff:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      email: staffMember.email,
      name: staffMember.name || `${staffMember.first_name || ''} ${staffMember.last_name || ''}`.trim(),
      role: staffMember.role,
      phone: staffMember.phone || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce conseiller ?')) return;
    try {
      await adminAPI.deleteStaff(id);
      toast.success('Conseiller supprimé');
      loadStaff();
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleResetPassword = async (id) => {
    try {
      await adminAPI.sendResetEmail(id);
      toast.success('Email de réinitialisation envoyé');
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Erreur lors de l\'envoi de l\'email');
    }
  };

  const resetForm = () => {
    setEditingStaff(null);
    setFormData({
      email: '',
      name: '',
      role: 'SUPPORT',
      phone: ''
    });
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      ADVISOR: { label: 'Conseiller', variant: 'default' },
      SUPPORT: { label: 'Support', variant: 'secondary' }
    };
    const config = roleConfig[role] || roleConfig.SUPPORT;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const advisors = staff.filter(s => s.role === 'ADVISOR');
  const supports = staff.filter(s => s.role === 'SUPPORT');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Conseillers</h1>
          <p className="text-muted-foreground">Gérez votre équipe de conseillers et support</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nouveau conseiller
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conseillers</CardTitle>
            <UserCog className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{advisors.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Support</CardTitle>
            <UserCog className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{supports.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ventes totales</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(advisors.reduce((sum, a) => sum + (a.total_sales || 0), 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advisors */}
      {advisors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Conseillers</CardTitle>
            <CardDescription>Équipe de conseillers commerciaux</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {advisors.map((advisor) => (
                <div
                  key={advisor.id}
                  className="flex items-center gap-4 p-4 rounded-lg border"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <UserCog className="h-6 w-6 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">{advisor.name || `${advisor.first_name || ''} ${advisor.last_name || ''}`.trim()}</p>
                      {getRoleBadge(advisor.role)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {advisor.email}
                      </div>
                      {advisor.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {advisor.phone}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <div>
                        <span className="font-medium">{advisor.orders_count || 0}</span> commandes
                      </div>
                      <div>
                        <span className="font-medium">{((advisor.total_sales || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€</span> de ventes
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleResetPassword(advisor.id)}>
                      <Key className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(advisor)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(advisor.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Support */}
      {supports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Support</CardTitle>
            <CardDescription>Équipe de support client</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {supports.map((support) => (
                <div
                  key={support.id}
                  className="flex items-center gap-4 p-4 rounded-lg border"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <UserCog className="h-6 w-6 text-secondary-foreground" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">{support.name || `${support.first_name || ''} ${support.last_name || ''}`.trim()}</p>
                      {getRoleBadge(support.role)}
                                        <Button variant="outline" size="sm" onClick={() => handleResetPassword(support.id)}>
                                          <Key className="h-4 w-4" />
                                        </Button>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {support.email}
                      </div>
                      {support.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {support.phone}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(support)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(support.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingStaff ? 'Modifier le conseiller' : 'Nouveau conseiller'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Rôle</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADVISOR">Conseiller</SelectItem>
                  <SelectItem value="SUPPORT">Support</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {editingStaff ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
